import type { QualificationAttribute } from '../../types/JobAttribute';
import type { QualificationCollectionInitialData } from '../../types/ProviderParams';

import { MatchQualification, MatchQualificationVariant } from './MatchQualification';
import { type BoxRef, Box, Heading, List, type ListItem, Pressable, Text } from '../../../../../../../mads';
import { useI18n, useLocale } from '../../../../../../../shared/I18n';
import { useTareData } from '../../../../../../../shared/native-modules/tare/TareDataContext';
import Sentiment from '../../../../../../../shared/util/salary/Sentiment';
import { useViewjobProctorAllocation } from '../../../../../../proctor/__generated__/ViewjobProctorContext';
import { 
    MobVjMatchOverviewTst, 
    MobVjMatchOverviewA11yListTog 
} from '../../../../../../proctor/__generated__/ViewjobProctorSpecifications';
import {
    useIsDesktopDisplay,
    useViewjobOneGraphModel,
} from '../../../../../context/ViewJobDataContext';
import { AskCareerScoutStarters } from '../../../AskCareerScoutStarters/AskCareerScoutStarters';
import {
    CAREER_SCOUT_ALWAYS_AWARE_BUCKETS,
    useCareerScoutAlwaysAwareBucket,
} from '../../../AskCareerScoutStarters/proctor';
import { ViewJobDivider } from '../../../Divider';
import { TextLink } from '../../../JobComponent/TextLink/TextLink';
import { BASE_PROVIDER_ID } from '../../constants/common';
import ViewType from '../../constants/ViewType';
import { JMUIContextProviders } from '../../context/JMUIContextProviders';
import { useFeatureFlags } from '../../context/useFeatureFlags';
import { useLogging } from '../../context/useLogging';
import { getInsightsModel } from '../../models/InsightsComponentModel';
import JobTagStrictness from '../../requirements/JobTagStrictness';
import InlineQuestionResponse from '../../types/InlineQuestionResponse';
import { getAttributesWithSentimentDiscrepancies } from '../QualificationCollection/util';
import { useQualificationSections } from '../QualificationCollection/util/useQualificationSections';

import { colors } from '@indeed/ifl-tokens';
import { AvroOptional } from '@indeed/signals-logger';
import React, { useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, findNodeHandle, StyleSheet } from 'react-native';

export type MatchOverviewQualificationCollectionProps = Pick<
    QualificationCollectionInitialData,
    'attributes' | 'viewType'
>;

/**
 * New variant of QualificationCollection for the Match Overview test.
 * This component will display when mob_vj_match_overview_tst is in grp1 or grp2.
 */
export const MatchOverviewQualificationCollection: React.FC<
    MatchOverviewQualificationCollectionProps
> = ({ attributes, viewType }) => {
    const logger = useLogging();
    const gt = useI18n();
    const ATTRIBUTE_DISPLAY_THRESHOLD = 5;
    const [showAllAttributes, setShowAllAttributes] = useState(false);
    const firstExpandedItemRef = useRef<BoxRef | null>(null);
    const isDesktop = useIsDesktopDisplay();

    // Get the match overview test value to determine sentiment sort order
    const matchOverviewTestValue = useViewjobProctorAllocation(MobVjMatchOverviewTst).value;

    // Gate for translated '${pos} of ${len}' a11y string in MADS List component.
    const isA11yPosTogActive = useViewjobProctorAllocation(MobVjMatchOverviewA11yListTog).value > 0;

    const isMatchSectionVariant =
        useCareerScoutAlwaysAwareBucket() === CAREER_SCOUT_ALWAYS_AWARE_BUCKETS.MATCH_SECTION;

    const { sentimentCounts, inlineSubmitCallback, onModalResetCallback, localAttributes } =
        useQualificationSections({
            attributes,
            viewType,
        });

    useEffect(() => {
        logger.logShowedProfileInsights();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const discrepancySuids = getAttributesWithSentimentDiscrepancies(attributes, localAttributes);
    const profileBaseUrl = useViewjobOneGraphModel()?.jobSeeker?.profile?.profileBaseUrl ?? '';
    // Sort attributes: first by strictness (REQUIRED first), then by sentiment
    const sortedAttributes = [...attributes].sort((a, b) => {
        // First, sort by strictness (REQUIRED first)
        const aIsRequired = a.strictness === JobTagStrictness.REQUIRED;
        const bIsRequired = b.strictness === JobTagStrictness.REQUIRED;

        if (aIsRequired !== bIsRequired) {
            return aIsRequired ? -1 : 1;
        }

        // Within the same strictness group, sort by sentiment based on test value
        const sentimentOrder =
            matchOverviewTestValue === 1
                ? [Sentiment.UNKNOWN, Sentiment.POSITIVE, Sentiment.NEGATIVE]
                : matchOverviewTestValue === 2
                  ? [Sentiment.NEGATIVE, Sentiment.UNKNOWN, Sentiment.POSITIVE]
                  : [Sentiment.UNKNOWN, Sentiment.POSITIVE, Sentiment.NEGATIVE]; // default

        const aIndex = sentimentOrder.indexOf(a.sentiment);
        const bIndex = sentimentOrder.indexOf(b.sentiment);

        return aIndex - bIndex;
    });

    const currentSentimentMap = new Map(
        localAttributes.map((attribute) => [attribute.suid, attribute.sentiment]),
    );

    const attributesToDisplay: ListItem[] = sortedAttributes.map((attribute, index) => ({
        key: 'MatchQualification' + attribute.suid,
        content: (
            <Box ref={index === ATTRIBUTE_DISPLAY_THRESHOLD ? firstExpandedItemRef : undefined}>
                <MatchQualification
                    variant={
                        currentSentimentMap.get(attribute.suid) === Sentiment.POSITIVE
                            ? MatchQualificationVariant.Matching
                            : currentSentimentMap.get(attribute.suid) === Sentiment.NEGATIVE
                            ? MatchQualificationVariant.Mismatching
                            : MatchQualificationVariant.Missing
                    }
                    primaryText={attribute.label}
                    required={attribute.strictness === JobTagStrictness.REQUIRED}
                    data-testid="match-qualification-matching"
                    onConfirm={() => {
                        void inlineSubmitCallback({
                            response: InlineQuestionResponse.YES,
                            attribute,
                        });
                    }}
                    onReject={() => {
                        void inlineSubmitCallback({
                            response: InlineQuestionResponse.NO,
                            attribute,
                        });
                    }}
                    onUndo={
                        discrepancySuids.includes(attribute.suid)
                            ? () => {
                                void onModalResetCallback(attribute);
                            }
                            : undefined
                    }
                />
            </Box>
        ),
    }));

    const subHeaderLevel = isDesktop ? 3 : 2;
    const textLevel = isDesktop ? 3 : 2;

    return (
        <>
            <Box sx={styles.container}>
                <Heading level={4} sx={styles.heading}>
                    {gt.gettext('Match overview')}
                </Heading>

                <Text level={textLevel} sx={styles.description}>
                    {gt.gettext("Here's how the job details align with your ")}
                    <TextLink
                        targetUrl={profileBaseUrl}
                        onPress={() => logger.logProfileNavClick()}
                        inline={true}
                        variant="standard"
                        level={textLevel}
                    >
                        {gt.gettext('profile')}
                    </TextLink>
                    .
                </Text>

                <Heading level={subHeaderLevel} sx={styles.subHeading}>
                    {gt.gettext('Qualifications: {0} of {1}', [
                        sentimentCounts.positive,
                        sentimentCounts.total,
                    ])}
                </Heading>

                <List
                    data={
                        showAllAttributes
                            ? attributesToDisplay
                            : attributesToDisplay.slice(0, ATTRIBUTE_DISPLAY_THRESHOLD)
                    }
                    a11yLabels={{
                        description: gt.gettext('Qualifications'),
                        ...(isA11yPosTogActive && {
                            positionText: gt.gettext('${pos} of ${len}'),
                        }),
                    }}
                    isVirtualized={false}
                />

                {!showAllAttributes && attributesToDisplay.length > ATTRIBUTE_DISPLAY_THRESHOLD && (
                    <Pressable
                        onPress={() => {
                            setShowAllAttributes(true);
                            setTimeout(() => {
                                const node = findNodeHandle(firstExpandedItemRef.current);
                                if (node) {
                                    AccessibilityInfo.setAccessibilityFocus(node);
                                }
                            }, 0);
                        }}
                        sx={styles.showMoreButton}
                        accessibilityRole="button"
                    >
                        <Text sx={styles.showMoreText} level={3}>
                            {gt.gettext('+{0} more', [
                                attributesToDisplay.length - ATTRIBUTE_DISPLAY_THRESHOLD,
                            ])}
                        </Text>
                    </Pressable>
                )}

                {isMatchSectionVariant && (
                    <Box sx={styles.startersContainer}>
                        <AskCareerScoutStarters />
                    </Box>
                )}
            </Box>
            <ViewJobDivider />
        </>
    );
};

const styles = StyleSheet.create({
    container: {
        marginBlock: 24,
    },
    heading: {
        marginBlockEnd: 8,
    },
    description: {
        marginBlockEnd: 16,
    },
    subHeading: {
        marginBlockEnd: 12,
    },
    showMoreButton: {
        marginTop: 4,
        flex: 1,
        justifyContent: 'center',
        alignItems: 'flex-start',
    },
    showMoreText: {
        color: colors.foreground['[base]'],
        borderBottomWidth: 1,
        borderStyle: 'dotted',
        marginTop: 8,
    },
    startersContainer: {
        marginTop: 16,
    },
});

export const MatchOverviewQualificationCollectionWithContext: React.FC = () => {
    return (
        <JMUIContextProviders providerId={BASE_PROVIDER_ID}>
            <MatchOverviewQualificationCollectionRenderer />
        </JMUIContextProviders>
    );
};

export const MatchOverviewQualificationCollectionRenderer: React.FC = () => {
    const vjModel = useViewjobOneGraphModel();
    const tareData = useTareData();
    const { country, language } = useLocale();
    const featureFlags = useFeatureFlags();
    const logger = useLogging();

    let shouldRender = false;
    let attributes: QualificationAttribute[] = [];
    let viewType: ViewType = ViewType.UNKNOWN;
    let oneGraphMatchComparison:
        | component.oneGraphMatchComparison.OneGraphMatchComparison
        | undefined;
    let isJobExpired: boolean = false;

    if (vjModel) {
        oneGraphMatchComparison = vjModel.jobSeeker.matchComparison;

        isJobExpired = vjModel.job.isExpired ?? false;
        const jobSeekerSuidsToFactIds =
            vjModel.jobSeeker.preferences?.jobSeekerSuidsToFactIds || [];
        const isProfileInsightsBlocked = vjModel.jobSeeker.profile.isProfileInsightsBlocked;
        const loggedIn = tareData.accountStatus?.isLoggedIn ?? false;

        // Check if we should continue with rendering
        if (oneGraphMatchComparison && !isJobExpired && !isProfileInsightsBlocked) {
            const insightsModel = getInsightsModel(
                featureFlags,
                loggedIn,
                oneGraphMatchComparison,
                jobSeekerSuidsToFactIds,
            );

            if (insightsModel.isEnabled) {
                shouldRender = true;
                attributes = insightsModel.attributes;
                viewType = insightsModel.viewType;
            }
        }
    }

    useEffect(() => {
        logger.logProviderRequest(
            {
                jobKey: vjModel?.job.jobKey || '',
                candEvalModel: AvroOptional.string(
                    oneGraphMatchComparison?.encouragementToApply?.strategy,
                ),
                candEvalRawScore: AvroOptional.double(
                    oneGraphMatchComparison?.encouragementToApply?.score,
                ),
                candEvalTrafficLightSignal: AvroOptional.string(
                    oneGraphMatchComparison?.encouragementToApply?.trafficLight,
                ),
                isJobExpired: AvroOptional.int(isJobExpired ? 1 : 0),
                jobCountry: vjModel?.job.jobCountry || '',
                jobLanguage: vjModel?.job.jobLanguage || '',
                occupationIds: vjModel?.job.jobOccupations?.join(',') || '',
                requestCountry: country || '',
                requestLanguage: language || '',
                resumeType: 'NO_RESUME',
                shouldShowInsightsComponent: shouldRender ? 1 : 0,
                taxonomyEntitiesCount: oneGraphMatchComparison?.attributeComparisons?.length || 0,
            },
            attributes,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return shouldRender ? (
        <MatchOverviewQualificationCollection attributes={attributes} viewType={viewType} />
    ) : null;
};
